/**
 * Semantic page model. Produces a description of the page's interactive CONTROLS, never its
 * CONTENTS.
 *
 * The single most important property of this file: there is no code path that reads an
 * element's value into the payload. A field contributes a `filled` boolean and nothing more.
 * Anything derived from text is passed through the redactor before it leaves.
 */
import { Redactor, isSensitiveField } from "./masking.js";
import type { PrivacyRules } from "./types.js";

export interface ModelElement {
  /** Stable-per-scan index used to address the element in a highlight request. */
  eid: string;
  label: string;
  role: string;
  tag: string;
  /** Present for inputs. TRUE means "has user content", never what that content is. */
  filled?: boolean;
  sensitive?: boolean;
  disabled?: boolean;
  /** The form will not submit without this one. Lets the assistant say what is missing. */
  required?: boolean;
  /**
   * FALSE when the page is currently rejecting this field. Present only when a field is
   * actually invalid, so the common case costs nothing on the wire.
   */
  valid?: boolean;
  /**
   * The rejection text the customer can see — "Enter a valid IFSC". It is the page's own
   * words, not the customer's, and it goes through the redactor like every other string.
   * Without it the assistant knows something is wrong but not what, which on a voice call
   * is barely better than knowing nothing.
   */
  error?: string;
  /** The field the customer is on right now. */
  focused?: boolean;
  /**
   * What KIND of thing this field wants, and in what shape.
   *
   * A label tells the assistant a field exists; it does not say that a card number is
   * sixteen digits, that this OTP box takes six, or that an IFSC is four letters, a
   * zero, then six characters. A chat agent can afford to be vague and correct itself.
   * A voice agent has to say the right thing first time — "enter your 16-digit card
   * number" — or the customer types something wrong and has to start over.
   *
   * Derived from what the page already declares: autocomplete, inputmode, maxlength,
   * pattern, type. `data-cobrowse-semantic` overrides it where the markup is silent.
   */
  semantic?: { kind: string; expects?: string };
  inView: boolean;
  rect: { x: number; y: number; w: number; h: number };
}

/**
 * A region of the page the SDK is structurally unable to describe.
 *
 * Almost always a cross-origin payment iframe — Razorpay, PayU, Billdesk — which is how
 * essentially every Indian checkout takes card details. `querySelectorAll` cannot cross
 * an origin boundary, and no amount of SDK code changes that.
 *
 * What matters is that the assistant is TOLD. Silence here is the worst outcome: the
 * page model simply looks like a page with nothing on it, so the assistant cheerfully
 * says "enter your card number" and the customer says "where?" — during the one step
 * where they most needed help. Reported as a blind region, the assistant can instead say
 * "I can't see inside the payment window, tell me what you see".
 */
export interface BlindRegion {
  kind: "cross-origin-frame";
  /** Origin of the frame, when the browser will tell us. Never a full URL. */
  origin?: string;
  /** Whether it is on screen right now — a hidden prefetch frame is not the story. */
  inView: boolean;
  rect: { x: number; y: number; w: number; h: number };
}

export interface PageModel {
  url: string;
  title: string;
  elements: ModelElement[];
  /** Present only when something on the page cannot be described. See BlindRegion. */
  blindRegions?: BlindRegion[];
  /** Which field currently has focus, by label. Helps the assistant say "you are on X". */
  activeFieldLabel?: string;
  /**
   * Signature of the visible controls AND their filled/valid state. Distinguishes one screen from
   * the next in a single-URL application, and — critically — changes when a field becomes
   * filled. Keying on labels alone meant typing into a field produced an identical signature,
   * the re-publish was skipped, and the assistant never learned the step was completed.
   * Validity is in for the same reason: a field turning red is a change the assistant has
   * to hear about, or the customer sits looking at an error nobody is helping them fix.
   */
  signature: string;
}

const INTERACTIVE =
  "a[href],button,input,select,textarea,summary," +
  "[role=button],[role=link],[role=checkbox],[role=radio],[role=tab],[role=menuitem]," +
  "[role=switch],[role=option],[role=combobox],[role=textbox],[role=slider],[role=spinbutton]," +
  "[contenteditable=''],[contenteditable=true]," +
  "[onclick],[tabindex]:not([tabindex='-1'])";

const MAX_ELEMENTS = 250;      // bound the payload on very large pages
const MAX_LABEL = 120;
// getComputedStyle is not free, so the pointer sweep is bounded on both ends: how many
// nodes we inspect, and how many extras we are willing to add to the model.
const POINTER_SCAN_LIMIT = 1500;
// Shadow roots nest (a component inside a component); bound the walk.
const SHADOW_MAX_DEPTH = 6;
const MAX_POINTER_ELEMENTS = 40;

export class PageScanner {
  private redactor: Redactor;
  private maskSelectors: string[] = [];
  private excludeRegions: string[] = [];
  private maskInputTypes = new Set<string>();
  private maskSensitiveLabels = false;

  constructor(rules?: PrivacyRules) {
    this.redactor = new Redactor(rules);
    this.update(rules);
  }

  update(rules?: PrivacyRules): void {
    this.redactor.update(rules);
    this.maskSelectors = rules?.maskSelectors ?? [];
    this.excludeRegions = rules?.excludeRegions ?? [];
    this.maskInputTypes = new Set((rules?.maskInputTypes ?? []).map((t) => t.toLowerCase()));
    this.maskSensitiveLabels = rules?.maskSensitiveLabels === true;
  }

  private matches(el: Element, selectors: string[]): boolean {
    for (const s of selectors) {
      try { if (el.closest(s)) return true; } catch { /* invalid selector: ignore, never throw */ }
    }
    return false;
  }

  /** Excluded regions and opted-out subtrees are removed from the model ENTIRELY. */
  private isExcluded(el: Element): boolean {
    if (el.closest("[data-cobrowse-ignore]")) return true;
    return this.matches(el, this.excludeRegions);
  }

  private isVisible(el: Element): boolean {
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return false;
    const s = getComputedStyle(el);
    if (s.visibility === "hidden" || s.visibility === "collapse" || s.display === "none") return false;
    if (Number(s.opacity || "1") === 0) return false;
    // Hidden from assistive technology is hidden from us. aria-hidden marks decorative
    // duplicates; `inert` marks a subtree behind a modal — pointing at either sends the
    // customer to a control they cannot use. Both inherit, so check ancestors.
    try {
      if (el.closest("[aria-hidden='true']")) return false;
      if (el.closest("[inert]")) return false;
      if ((el as HTMLElement).hidden) return false;
    } catch { /* closest unsupported: fall through */ }
    return true;
  }

  /**
   * Query that pierces open shadow roots.
   *
   * `document.querySelectorAll` stops at every shadow boundary, so an application built on
   * web components — Shoelace, Ionic, Salesforce Lightning, most modern design systems —
   * is entirely invisible to it. The assistant would report a page with no controls on it.
   */
  private deepQuery(selector: string): HTMLElement[] {
    const out: HTMLElement[] = [];
    const visit = (root: Document | ShadowRoot, depth: number): void => {
      if (depth > SHADOW_MAX_DEPTH) return;
      let found: NodeListOf<HTMLElement>;
      try { found = root.querySelectorAll<HTMLElement>(selector); } catch { return; }
      for (const el of found) out.push(el);
      let hosts: NodeListOf<HTMLElement>;
      try { hosts = root.querySelectorAll<HTMLElement>("*"); } catch { return; }
      for (const host of hosts) {
        const sr = host.shadowRoot;                 // closed roots are correctly unreachable
        if (sr) visit(sr, depth + 1);
      }
    };
    visit(document, 0);
    return out;
  }

  /**
   * Accessible name, in the order a screen reader would resolve it. The host can override with
   * data-cobrowse-label. Note what is NOT consulted: the element's value.
   */
  private labelFor(el: HTMLElement): string {
    const explicit = el.getAttribute("data-cobrowse-label");
    if (explicit) return explicit;
    const aria = el.getAttribute("aria-label");
    if (aria) return aria;
    const labelledBy = el.getAttribute("aria-labelledby");
    if (labelledBy) {
      const t = labelledBy.split(/\s+/).map((id) => document.getElementById(id)?.textContent || "").join(" ").trim();
      if (t) return t;
    }
    if (el.id) {
      const lbl = document.querySelector(`label[for="${CSS.escape(el.id)}"]`);
      if (lbl?.textContent?.trim()) return lbl.textContent.trim();
    }
    const wrapping = el.closest("label");
    if (wrapping?.textContent?.trim()) return wrapping.textContent.trim();
    const ph = el.getAttribute("placeholder");
    if (ph) return ph;
    // Icon-only controls carry their name on the image inside them — an alt or aria-label
    // on a descendant img/svg. Without this, every icon button in the app is nameless and
    // therefore unpointable.
    try {
      const inner = el.querySelector("img[alt],svg[aria-label],img[aria-label],use[aria-label]");
      const innerName = inner?.getAttribute("alt") || inner?.getAttribute("aria-label");
      if (innerName?.trim()) return innerName.trim();
    } catch { /* ignore */ }
    const title = el.getAttribute("title");
    if (title) return title;
    // A field inside a fieldset takes its meaning from the legend ("Beneficiary details"),
    // which is often the only thing distinguishing two identical-looking inputs.
    try {
      const legend = el.closest("fieldset")?.querySelector("legend")?.textContent?.trim();
      if (legend) return legend;
    } catch { /* ignore */ }
    // Text content last, and only its own, so a container does not absorb a whole page.
    const own = (el.textContent || "").trim();
    if (own && own.length <= MAX_LABEL * 2) return own;
    return "";
  }

  private roleFor(el: HTMLElement): string {
    const explicit = el.getAttribute("role");
    if (explicit) return explicit;
    const tag = el.tagName.toLowerCase();
    if (tag === "a") return "link";
    if (tag === "button") return "button";
    if (tag === "select") return "combobox";
    if (tag === "textarea") return "textbox";
    if (tag === "input") {
      const t = ((el as HTMLInputElement).type || "text").toLowerCase();
      if (t === "checkbox" || t === "radio" || t === "submit" || t === "button") return t;
      return "textbox";
    }
    return "generic";
  }

  /** Has the user put something in this field? A boolean, never the content. */
  private isFilled(el: HTMLElement): boolean | undefined {
    const tag = el.tagName.toLowerCase();
    if (tag === "input") {
      const i = el as HTMLInputElement;
      const t = (i.type || "text").toLowerCase();
      if (t === "checkbox" || t === "radio") return i.checked;
      return i.value.length > 0;          // length only. The value itself is never read out.
    }
    if (tag === "textarea") return (el as HTMLTextAreaElement).value.length > 0;
    if (tag === "select") return (el as HTMLSelectElement).selectedIndex > 0;
    return undefined;
  }

  /**
   * Is the page currently rejecting this field, and what is it saying?
   *
   * Three independent signals, because no single one is reliable across frameworks:
   * `aria-invalid` is what accessible component libraries set, `checkValidity()` is what
   * native constraint validation knows, and an `.error`/`.invalid` container is what
   * everything else does. Returns undefined for a field that is fine, so a healthy page
   * adds nothing to the payload.
   */
  private validity(el: HTMLElement): { valid: false; error?: string } | undefined {
    const tag = el.tagName.toLowerCase();
    if (tag !== "input" && tag !== "select" && tag !== "textarea") return undefined;
    const input = el as HTMLInputElement;

    let invalid = el.getAttribute("aria-invalid") === "true";
    if (!invalid) {
      try { invalid = typeof input.checkValidity === "function" && !input.checkValidity(); }
      catch { /* a detached or exotic control: fall through to the class check */ }
    }
    let container: Element | null = null;
    if (!invalid) {
      try {
        container = el.closest(".error,.invalid,.has-error,.is-invalid,[data-error]");
        invalid = !!container;
      } catch { /* selector unsupported: leave it */ }
    }
    if (!invalid) return undefined;

    // The message, in the order a customer would actually read one.
    let error = "";
    try {
      const describedBy = el.getAttribute("aria-describedby");
      if (describedBy) {
        error = describedBy.split(/\s+/)
          .map((id) => document.getElementById(id)?.textContent?.trim() ?? "")
          .filter(Boolean).join(" ");
      }
      if (!error && container) {
        error = (container.querySelector(".error-message,.error-text,[role=alert]")?.textContent ?? "").trim();
      }
      if (!error) error = String(input.validationMessage ?? "").trim();
    } catch { /* no message is still a useful signal */ }

    return { valid: false, error: error ? this.redactor.text(error).slice(0, MAX_LABEL) : undefined };
  }

  /**
   * Clickable elements that the selector above cannot see.
   *
   * React attaches click handlers synthetically, so `<div onClick={...}>` carries no
   * `onclick` attribute, no role and no tabindex — it is invisible to any attribute-based
   * selector. That is not an edge case: card pickers, tiles and list rows are written this
   * way in most React applications, and an assistant that cannot see them cannot guide
   * anyone through them.
   *
   * An explicit `cursor: pointer` is the one signal such elements reliably carry. Two rules
   * keep it from flooding the model: `cursor` inherits, so we take only the OUTERMOST
   * pointer element in a subtree, and we skip anything that contains a real control — there
   * the inner button is the better target.
   */
  private pointerClickables(already: HTMLElement[]): HTMLElement[] {
    const seen = new Set(already);
    const out: HTMLElement[] = [];
    let candidates: HTMLElement[] = [];
    try {
      candidates = Array.from(document.querySelectorAll<HTMLElement>("div,li,section,article,td,tr"));
    } catch { return out; }
    if (candidates.length > POINTER_SCAN_LIMIT) candidates = candidates.slice(0, POINTER_SCAN_LIMIT);

    const isPointer = (el: Element | null): boolean => {
      if (!el) return false;
      try { return getComputedStyle(el).cursor === "pointer"; } catch { return false; }
    };

    for (const el of candidates) {
      if (out.length >= MAX_POINTER_ELEMENTS) break;
      if (seen.has(el)) continue;
      if (!isPointer(el)) continue;
      if (isPointer(el.parentElement)) continue;          // inherited — keep the outermost only
      try { if (el.querySelector(INTERACTIVE)) continue; } catch { /* keep it */ }
      out.push(el);
    }
    return out;
  }

  scan(): PageModel {
    const out: ModelElement[] = [];
    const vh = window.innerHeight || 0;
    const vw = window.innerWidth || 0;
    let nodes: HTMLElement[] = [];
    try {
      nodes = this.deepQuery(INTERACTIVE);
      nodes = nodes.concat(this.pointerClickables(nodes));
    } catch { nodes = []; }

    const active = document.activeElement as HTMLElement | null;

    let i = 0;
    for (const el of nodes) {
      if (out.length >= MAX_ELEMENTS) break;
      if (this.isExcluded(el)) continue;
      if (!this.isVisible(el)) continue;

      const sensitive = isSensitiveField(el, this.maskInputTypes);
      // A field's NAME and its VALUE are different things. "Card number" is a label, not a
      // secret, and the assistant has to be able to say "tap the highlighted card number
      // field". So being sensitive strips the VALUE (never read) and forbids the agent from
      // filling it, but keeps the label. Only an explicit host rule removes the label too:
      // that is the integrator saying the field's very name reveals something.
      const masked = this.matches(el, this.maskSelectors) ||
        (this.maskSensitiveLabels && sensitive) ||
        (el.tagName.toLowerCase() === "input" &&
         this.maskInputTypes.has(((el as HTMLInputElement).type || "text").toLowerCase()));
      const r = el.getBoundingClientRect();

      const bad = this.validity(el);

      out.push({
        eid: `e${i++}`,
        // A masked element keeps its existence and its filled state, and loses its label.
        label: masked ? "" : this.redactor.text(this.labelFor(el)).slice(0, MAX_LABEL),
        role: this.roleFor(el),
        tag: el.tagName.toLowerCase(),
        filled: this.isFilled(el),
        sensitive: sensitive || undefined,
        disabled: (el as HTMLInputElement).disabled || el.getAttribute("aria-disabled") === "true" || undefined,
        required: (el as HTMLInputElement).required || el.getAttribute("aria-required") === "true" || undefined,
        valid: bad ? false : undefined,
        // A masked field's error text is suppressed with its label: the message often
        // quotes the value ("4111 1111 is not a valid card number").
        error: bad && !masked ? bad.error : undefined,
        focused: el === active || undefined,
        // Suppressed for a masked field along with its label: naming the shape of a box
        // whose very name the integrator chose to hide gives it straight back.
        semantic: masked ? undefined : this.semanticFor(el),
        inView: r.top < vh && r.bottom > 0 && r.left < vw && r.right > 0,
        rect: { x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.width), h: Math.round(r.height) },
      });
    }

    const blind = this.blindRegions();

    let activeFieldLabel: string | undefined;
    if (active && active !== document.body && !this.isExcluded(active) && !isSensitiveField(active, this.maskInputTypes)) {
      activeFieldLabel = this.redactor.text(this.labelFor(active)).slice(0, MAX_LABEL) || undefined;
    }

    // Path only. Query and fragment can carry identifiers and are dropped.
    let url = "";
    try { url = location.origin + location.pathname; } catch { /* non-browser context */ }

    return {
      url,
      title: this.redactor.text(document.title).slice(0, MAX_LABEL),
      elements: out,
      blindRegions: blind.length ? blind : undefined,
      activeFieldLabel,
      signature: out
        .map((e) => `${e.label}\u0001${e.filled === undefined ? "" : e.filled ? "1" : "0"}${e.valid === false ? "!" : ""}`)
        .sort().join("|").slice(0, 900) +
        // The focused field belongs in the signature too, and for the same reason.
        // Moving between fields changes no label and no filled state, so the publish was
        // skipped and `activeFieldLabel` never actually moved on the wire — the SDK
        // scheduled a rescan on every focusin and then threw the result away. An
        // assistant that cannot say "you're on the IFSC box now" is guessing.
        `@${activeFieldLabel ?? ""}` +
        // A payment iframe appearing is the most important change on the whole page, and
        // it adds no elements — so without this the signature is unchanged, the publish
        // is skipped, and the assistant is never told it has gone blind.
        `#${blind.filter((b) => b.inView).length}`,
    };
  }

  /**
   * The label we are permitted to REPORT for an element, applying the same rules as the page
   * model. Returns null when the element must not be described at all. Event emission goes
   * through here so an interaction can never leak what the model itself would have masked.
   */
  reportableLabel(el: HTMLElement): string | null {
    if (this.isExcluded(el)) return null;
    if (this.matches(el, this.maskSelectors)) return null;
    if (this.maskSensitiveLabels && isSensitiveField(el, this.maskInputTypes)) return null;
    if (el.tagName.toLowerCase() === "input" &&
        this.maskInputTypes.has(((el as HTMLInputElement).type || "text").toLowerCase())) return null;
    const label = this.redactor.text(this.labelFor(el)).slice(0, MAX_LABEL).trim();
    return label || null;
  }

  /**
   * What kind of value this field wants, and its shape, in words an assistant can say.
   *
   * Reads only what the page already declares about ITSELF — never anything the customer
   * typed. An explicit `data-cobrowse-semantic` wins, because the integrator knows their
   * own markup better than any heuristic does.
   */
  private semanticFor(el: HTMLElement): { kind: string; expects?: string } | undefined {
    const tag = el.tagName.toLowerCase();
    if (tag !== "input" && tag !== "select" && tag !== "textarea") return undefined;
    const input = el as HTMLInputElement;

    const explicit = el.getAttribute("data-cobrowse-semantic");
    if (explicit) return { kind: explicit.trim().slice(0, 24) };

    const ac = (el.getAttribute("autocomplete") || "").toLowerCase();
    const hint = [input.name, input.id, el.getAttribute("aria-label"), ac].filter(Boolean).join(" ").toLowerCase();
    const maxLen = Number(el.getAttribute("maxlength") || 0);
    const digits = (n: number) => `${n} digits`;

    // Ordered most-specific first: "cardholder name" must not be read as a card number.
    if (ac.includes("cc-csc") || /\bcvv\b|\bcvc\b|security code/.test(hint)) {
      return { kind: "cvv", expects: "3 digits on the back of the card" };
    }
    if (ac.includes("cc-exp") || /expiry|expiration|valid thru/.test(hint)) {
      return { kind: "expiry", expects: "month and year, MM/YY" };
    }
    if (ac.includes("cc-number") || /card ?number|cardnumber/.test(hint)) {
      return { kind: "card", expects: maxLen >= 19 ? "16 digits, in groups of four" : digits(16) };
    }
    if (ac.includes("one-time-code") || /\botp\b|one[- ]?time/.test(hint)) {
      return { kind: "otp", expects: maxLen > 0 ? digits(maxLen) : "the code that was texted to you" };
    }
    if (/\bifsc\b/.test(hint)) return { kind: "ifsc", expects: "4 letters, a zero, then 6 characters" };
    if (/\bupi\b|\bvpa\b/.test(hint)) return { kind: "upi", expects: "something like name@bank" };
    if (/\bpan\b/.test(hint)) return { kind: "pan", expects: "5 letters, 4 digits, then a letter" };
    if (/aadhaar|aadhar/.test(hint)) return { kind: "aadhaar", expects: digits(12) };
    if (/\bpin\b|passcode/.test(hint)) return { kind: "pin", expects: maxLen > 0 ? digits(maxLen) : undefined };
    if (/\bifsc|account ?number|\ba\/c\b/.test(hint)) return { kind: "account" };
    if (ac.includes("tel") || input.type === "tel" || /mobile|phone/.test(hint)) {
      return { kind: "phone", expects: digits(10) };
    }
    if (ac.includes("email") || input.type === "email" || /e-?mail/.test(hint)) return { kind: "email" };
    if (input.type === "date" || /\bdob\b|date of birth/.test(hint)) return { kind: "date" };
    if (/amount|\bemi\b|\brs\b|₹/.test(hint)) return { kind: "amount" };
    if (ac.includes("name") || /\bname\b/.test(hint)) return { kind: "name" };
    return undefined;
  }

  /**
   * Frames whose contents we cannot read.
   *
   * Reachability is decided by trying, not by comparing URLs: a same-origin frame is
   * readable and needs no report, and the SecurityError a cross-origin one throws is the
   * only honest test. Excluded and hidden frames are skipped — a blind region the
   * customer cannot see is not something the assistant should mention.
   */
  private blindRegions(): BlindRegion[] {
    const out: BlindRegion[] = [];
    let frames: HTMLIFrameElement[] = [];
    try { frames = Array.from(document.querySelectorAll("iframe,frame")); } catch { return out; }

    const vh = window.innerHeight || 0;
    const vw = window.innerWidth || 0;
    for (const f of frames) {
      if (out.length >= 4) break;
      if (this.isExcluded(f) || !this.isVisible(f)) continue;
      let readable = false;
      try { readable = !!f.contentDocument; } catch { readable = false; }
      if (readable) continue;                    // same-origin: scan() already saw into it

      let origin: string | undefined;
      try {
        const src = f.getAttribute("src") || "";
        // The origin only. A payment frame's query string carries order ids and amounts.
        if (/^https?:\/\//.test(src)) origin = new URL(src).origin;
      } catch { /* unparseable src: the region still counts */ }

      const r = f.getBoundingClientRect();
      out.push({
        kind: "cross-origin-frame",
        origin,
        inView: r.top < vh && r.bottom > 0 && r.left < vw && r.right > 0,
        rect: { x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.width), h: Math.round(r.height) },
      });
    }
    return out;
  }

  /**
   * The labels of the fields standing between the customer and a successful submit:
   * required-and-empty, or currently invalid.
   *
   * Scoped to one form when we know which; otherwise the whole document, because a
   * JavaScript-validated "form" is frequently not a <form> at all. Masked and excluded
   * fields contribute nothing — reportableLabel is the same gate the events use, so a
   * blocked submit can never name a control the model itself would have hidden.
   */
  blockers(form?: HTMLFormElement): string[] {
    const out: string[] = [];
    let nodes: HTMLElement[] = [];
    try {
      nodes = form
        ? Array.from(form.querySelectorAll<HTMLElement>("input,select,textarea"))
        : this.deepQuery("input,select,textarea");
    } catch { return out; }

    for (const el of nodes) {
      if (out.length >= 12) break;                      // an assistant cannot read a longer list aloud
      if (this.isExcluded(el) || !this.isVisible(el)) continue;
      if ((el as HTMLInputElement).disabled) continue;
      const required = (el as HTMLInputElement).required || el.getAttribute("aria-required") === "true";
      const empty = this.isFilled(el) === false;
      const blocked = (required && empty) || !!this.validity(el);
      if (!blocked) continue;
      const label = this.reportableLabel(el);
      if (label && !out.includes(label)) out.push(label);
    }
    return out;
  }

  /** Resolve a target label to a live element, for highlighting. Mirrors the service's scoring. */
  resolve(target: string): HTMLElement | null {
    const want = norm(target);
    if (!want) return null;
    let best: { el: HTMLElement; score: number; len: number } | null = null;
    let nodes: HTMLElement[] = [];
    try {
      nodes = this.deepQuery(INTERACTIVE);
      // Same blind spots as scan(): a control we can see, we must be able to point at.
      nodes = nodes.concat(this.pointerClickables(nodes));
    } catch { return null; }

    for (const el of nodes) {
      if (this.isExcluded(el) || !this.isVisible(el)) continue;
      // Sensitive fields are highlightable: pointing at the OTP box is exactly what the
      // assistant should do while refusing to read or type into it.
      if (this.matches(el, this.maskSelectors)) continue;
      const label = norm(this.labelFor(el));
      if (!label) continue;
      let score = 0;
      if (label === want) score = 30;
      else if (label.startsWith(want)) score = 20;
      else if (label.includes(want)) score = 10;
      if (score === 0) continue;
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) score += 5;      // in view
      const role = this.roleFor(el);
      if (role === "button" || role === "link") score += 18;           // prefer actionable
      if (el.tagName.toLowerCase() === "span") score -= 12;            // deprioritise prose
      if ((el as HTMLInputElement).disabled) score -= 8;
      const len = label.length;
      if (!best || score > best.score || (score === best.score && len < best.len)) best = { el, score, len };
    }
    return best?.el ?? null;
  }
}

function norm(s: string): string {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}
